<template>
  <block
    component-name="block-carousel-collapse"
    ref="tpl_root"
    :px="false"
    class="overflow-hidden"
    body-class="overflow-hidden"
  >
    <template v-slot:default="blockData">
      <div
        class="overflow-hidden w-full"
        v-surfer="{
          setup: {
            destroyOn: 'visible',
          },
          observers: {
            init: true,
            visible: { event: true },
            offscreenTop: { event: true },
            offscreenBottom: { event: true },
          },
        }"
        @surfer-visible="handleSurfer('visible', false)"
        @surfer-offscreen-top="handleSurfer('offscreen-top', true)"
        @surfer-offscreen-bottom="handleSurfer(null, true)"
      >
        <swiper-container
          init="false"
          class="w-full overflow-hidden"
          events-prefix="swiper-"
        >
          <swiper-slide v-for="(item, index) in $attrs.data.body.items">
            <card-collapse
              class="w-full"
              :title="item.title"
              :text="item.text"
              :footer="item.footer"
              :asset="{
                ...item.asset,
                loading:
                  index > 2 || blockData?.loading === 'lazy' ? 'lazy' : 'eager',
                sizes: {
                  base: 450,
                  phablet: 700,
                  tablet: 550,
                  laptop: 600,
                  desktop: 700,
                  extdesktop: 750,
                },
              }"
              :class="{
                'translate-y-[100vh]': index < 4,
                '!translate-y-0':
                  index < 4 &&
                  (surferState === 'visible' ||
                    surferState === 'offscreen-top'),
                'duration-[850ms] ease-in-out':
                  index < 4 && surferState === 'visible',
                'delay-100':
                  index === 1 &&
                  (surferState === 'visible' ||
                    surferState === 'offscreen-top'),
                'delay-200':
                  index === 2 &&
                  (surferState === 'visible' ||
                    surferState === 'offscreen-top'),
                'delay-300':
                  index === 3 &&
                  (surferState === 'visible' ||
                    surferState === 'offscreen-top'),
              }"
            />
          </swiper-slide>
        </swiper-container>

        <footer
          v-if="$attrs.data.body.items.length > 1"
          class="hidden laptop:flex items-end justify-end w-full pointer-events-none z-2 gap-lg pr-edge pt-2xl"
        >
          <wrapper-reveal class="pointer-events-auto" reveal="fade-from-right">
            <btn-arrow
              :disabled="currentIndex === 0"
              direction="prev"
              @click="handleCarouselProgress('prev')"
            />
          </wrapper-reveal>

          <wrapper-reveal class="pointer-events-auto" reveal="fade-from-left">
            <btn-arrow
              :disabled="currentIndex === $attrs.data.body.items.length - 1"
              direction="next"
              @click="handleCarouselProgress('next')"
            />
          </wrapper-reveal>
        </footer>
      </div>
    </template>
  </block>
</template>

<script setup>
const tpl_root = templateRef("tpl_root");
const tpl_wrapper = templateRef("tpl_wrapper");
const { height: wrapperH } = useElementSize(tpl_wrapper);
const { width: windowW } = useWindowSize();
const surferState = ref(null);

const offset = computed(() => {
  return Math.min(Math.max(24, (windowW.value / 100) * 4.16), 80);
});

const spaceBetween = computed(() => {
  return Math.min(Math.max(64, (windowW.value / 100) * 6.25), 120);
});

let swiperEl;

function handleSurfer(val, resetSlides = false) {
  surferState.value = val;
  if (resetSlides) swiperEl?.swiper?.slideTo(0, 0, false);
}

function handleCarouselProgress(nextPrev) {
  switch (nextPrev) {
    case "prev":
      swiperEl?.swiper.slidePrev();
      break;

    case "next":
      swiperEl?.swiper.slideNext();
      break;
  }
}

useSafeMountedEl(tpl_root, () => {
  swiperEl = tpl_root.value.$el.querySelector("swiper-container");
  Object.assign(swiperEl, {
    grabCursor: true,
    parallax: true,
    speed: 650,
    slidesOffsetBefore: offset.value,
    spaceBetween: spaceBetween.value,
    slidesOffsetAfter: offset.value,
    freeMode: true,
    slidesPerView: 1.4,
    breakpoints: {
      600: {
        slidesPerView: 1.8,
      },
      1020: {
        slidesPerView: 2.4,
      },
      1272: {
        slidesPerView: 2.6,
      },
      1512: {
        slidesPerView: 2.8,
      },
      1920: {
        slidesPerView: 3.6,
      },
    },
    on: {
      init() {
        ScrollTrigger.refresh(true);
      },
      resize(swiper) {
        swiperEl.slidesOffsetBefore = offset.value;
        swiperEl.slidesOffsetAfter = offset.value;
        swiperEl.spaceBetween = spaceBetween.value;
      },
    },
  });
  swiperEl.initialize();
});

onBeforeUnmount(() => {
  swiperEl?.swiper?.destroy();
  swiperEl = null;
});
</script>
